import type { ReactElement } from 'react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { MonitorPlay } from 'lucide-react';
import { api, SpaceSummary } from '../api';
import { StatusBadge } from '../components/StatusBadge';

const PUBLIC_TYPE_LABELS: Record<number, string> = {
  0: 'Private',
  1: 'Allow List',
  2: 'Public',
};

const PLACEHOLDER =
  'data:image/svg+xml,' +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" width="160" height="99" fill="none"><rect width="160" height="99" fill="#1e293b"/><text x="80" y="52" text-anchor="middle" fill="#475569" font-size="12" font-family="sans-serif">No Image</text></svg>',
  );

export function SpacesList(): ReactElement {
  const [spaces, setSpaces] = useState<SpaceSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async (): Promise<void> => {
      try {
        const list = await api.listSpaces();
        if (!cancelled) setSpaces(list);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'failed to load');
        }
      }
    };
    void load();
    // Cheap auto-refresh: poll every 5s so status badges and the list
    // reflect backend changes (publish/archive/reconcile) without a
    // full page reload. Backend SSE would replace this later.
    const timer = setInterval(() => {
      void load();
    }, 5000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, []);

  return (
    <section className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold">Spaces</h1>
        <Link
          to="/spaces/new"
          className="bg-accent text-slate-900 rounded px-3 py-1 text-sm font-medium"
        >
          New Space
        </Link>
      </div>
      {error && <p className="text-rose-400 text-sm">{error}</p>}
      {spaces === null ? (
        <p className="text-slate-400">Loading…</p>
      ) : spaces.length === 0 ? (
        <p className="text-slate-400">No Spaces yet.</p>
      ) : (
        <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {spaces.map((space) => {
            const imageUrl =
              space.featuredImages.length > 0
                ? space.featuredImages[0]!
                : PLACEHOLDER;

            return (
              <li
                key={space.id}
                className="bg-panel rounded-lg overflow-hidden flex flex-col"
              >
                {/* Featured image */}
                <Link to={`/spaces/${space.id}`} className="block">
                  <div className="w-full aspect-[1.618/1] bg-slate-800 overflow-hidden">
                    <img
                      src={imageUrl}
                      alt={space.title}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = PLACEHOLDER;
                      }}
                    />
                  </div>
                </Link>

                {/* Card body */}
                <div className="p-4 flex flex-col gap-2 flex-1">
                  <div className="flex items-center justify-between">
                    <Link
                      to={`/spaces/${space.id}`}
                      className="font-bold hover:text-accent transition-colors"
                    >
                      {space.title}
                    </Link>
                    <div className="flex items-center gap-1.5">
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-800 text-slate-400 border border-slate-700">
                        {PUBLIC_TYPE_LABELS[space.publicType] ?? 'Private'}
                      </span>
                      <StatusBadge status={space.status} />
                    </div>
                  </div>

                  <p className="text-sm text-slate-300 line-clamp-2">
                    {space.description ?? '(no description)'}
                  </p>

                  <div className="text-xs text-slate-500">
                    Actors {space.actorCount} · Commands {space.commandCount}
                  </div>

                  <div className="flex items-center gap-2 mt-auto pt-2 border-t border-slate-800">
                    <Link
                      to={`/spaces/${space.id}`}
                      className="text-accent text-sm hover:underline"
                    >
                      Edit
                    </Link>
                    <Link
                      to={`/spaces/${space.id}/dashboard`}
                      className="text-accent text-sm hover:underline"
                    >
                      Dashboard
                    </Link>
                    <Link
                      to={`/spaces/${space.id}/actors`}
                      className="text-accent text-sm hover:underline"
                    >
                      Actors
                    </Link>
                    <Link
                      to={`/spaces/${space.id}/timeline`}
                      className="inline-flex items-center gap-1 text-accent text-sm hover:underline ml-auto"
                    >
                      <MonitorPlay size={14} />
                      Timeline
                    </Link>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
